// `extension-automation-criteria-api.ng1.ts` — the automation ("auto-matching") configuration:
// which candidates the automation may put in front of a recruiter, and who Copilot emails.
//
// ⚠️ ONE REQUEST SHAPE FOR TWO VERY DIFFERENT CALLS. `getPrediction` and
// `saveAutoMatchingConfig` take the SAME body — the whole edited configuration — so the only
// thing separating "how many jobs would this match" from "make this the tenant's automation" is
// the path. Read the call site before changing either.
import { request } from './request.ts'

/**
 * `IExtensionCriteriaAtsFilter` — one row of the ATS-filter table.
 *
 * `data` is deliberately opaque here, as `extensionCriteria` is on the alert calls: its five
 * shapes belong to the editor that produces them (`packages/ui/src/search/atsFilterData.ts`,
 * `AtsFilterData`), and duplicating that union in the client would give two definitions to keep
 * in step for no checking the editor does not already do.
 */
export interface AutomationAtsFilter {
  section?: string
  data?: unknown
}

/** `IExtensionAutomationCandidateCriteria`. */
export interface AutomationCandidateCriteria {
  atsFilters?: readonly AutomationAtsFilter[]
  /** Only candidates whose owner is the job's owner. */
  candidateOwnerJobOwner: boolean
  /** ⚠️ DEFAULTS TO TRUE when the tenant has no saved configuration — see `initCandidateCriteria`. */
  excludeCandidateWithCurrentJobSubmission: boolean
}

/** `IExtensionAutomationCopilotConfigData`. */
export interface AutomationCopilotConfig {
  active: boolean
  /** ⚠️ DEFAULTS TO TRUE, unlike every other flag here. */
  sendToJobOwner: boolean
  sendToAssignedUsers: boolean
  /**
   * ⚠️ AN ARRAY IN THE DTO AND A COMMA-SEPARATED STRING IN THE FORM. The component maps one to
   * the other on the way in and out (`join(",")` / split), so a port that binds the input
   * straight to this field sends the string and the server stores one long recipient.
   */
  additionalRecipients?: string[]
  backupRecipient?: string
  jobCriteria?: { atsFilters?: readonly AutomationAtsFilter[] }
}

/** `IExtensionAutoMatchingConfigDataDto` — both halves, each absent until first saved. */
export interface AutoMatchingConfigData {
  automationCandidateCriteria?: AutomationCandidateCriteria
  automationCopilotConfig?: AutomationCopilotConfig
}

/**
 * `IExtensionAutoMatchingConfigSaveRequest`.
 *
 * ⚠️ THE TWO KEYS ARE NOT NAMED THE SAME WAY. It is `automationCandidateCriteria` and
 * `automationCopilotData` — "Data", not "Config", on the second — while the GET answers with
 * `automationCopilotConfig`. Mirroring the response's key here silently saves no Copilot
 * configuration at all.
 */
export interface AutoMatchingConfigSaveRequest {
  automationCandidateCriteria: AutomationCandidateCriteria
  automationCopilotData: AutomationCopilotConfig
}

export async function getAutoMatchingConfig(): Promise<AutoMatchingConfigData> {
  return request('GET', '/chrome-extension-api/automatching/config/getConfigData')
}

/** How many jobs the edited configuration would match. The counter at the top of the screen. */
export async function getAutoMatchingPrediction(
  params: AutoMatchingConfigSaveRequest,
): Promise<{ results: number }> {
  return request('POST', '/chrome-extension-api/automatching/config/getPrediction', params)
}

/**
 * Makes the edited configuration the tenant's automation.
 *
 * ⚠️ OUTWARD-FACING: it decides which candidates an automated process puts in front of
 * recruiters and who it emails. Nothing in this repo calls it — the Save button renders and is
 * not wired — and that is deliberate while the port reaches no user (see `AutomationCriteriaPage`).
 */
export async function saveAutoMatchingConfig(params: AutoMatchingConfigSaveRequest): Promise<void> {
  return request('POST', '/chrome-extension-api/automatching/config/saveAutoMatchingConfig', params)
}
